import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import {
  Card, Col, Divider, Row, Skeleton, Tag, Typography,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import useAuth from '../../../hooks/useAuth';
import useServices from '../../../hooks/useServices';
import { DateTimeUtils } from '../../../utils/dateTime';
import { PathBuilder } from '../../../utils/pathBuilder';
import StatCard from './StatCard';
import { BBA_PRIMARY } from '../../../themes/tokens';

const { Title, Text } = Typography;

const categoryColors: Record<string, string> = {
  seminar: 'success',
  social: 'warning',
  sport: 'processing',
  charity: 'error',
};

const greetByHour = (): string => {
  const h = dayjs().hour();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [events, setEvents] = useState<any[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [customerTotal, setCustomerTotal] = useState<number>(0);
  const [notificationTotal, setNotificationTotal] = useState<number>(0);

  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  const { service: eventService } = useServices(ServiceNameEnum.event.index);
  const { service: newsService } = useServices(ServiceNameEnum.news.index);
  const { service: customerService } = useServices(ServiceNameEnum.customerMgmt.index);
  const { service: notificationService } = useServices(ServiceNameEnum.notification.index);

  useEffect(() => {
    (async () => {
      const { data } = await eventService.get({});
      if (Array.isArray(data)) setEvents(data);
      setLoadingEvents(false);
    })();
    (async () => {
      const { data } = await newsService.get({});
      if (Array.isArray(data)) setNews(data);
      setLoadingNews(false);
    })();
    (async () => {
      const { data } = await customerService.get({ queryString: { page: 1 } });
      if (data?.pagination?.total !== undefined) setCustomerTotal(data.pagination.total);
      setLoadingCustomers(false);
    })();
    (async () => {
      const { data } = await notificationService.get({ queryString: { page: 1 } });
      if (data?.pagination?.total !== undefined) setNotificationTotal(data.pagination.total);
      setLoadingNotifications(false);
    })();
  }, []);

  const eventStats = useMemo(() => {
    const now = dayjs();
    const published = events.filter((e) => e.isPublished).length;
    const upcoming = events
      .filter((e) => e.startAt && dayjs(e.startAt).isAfter(now))
      .sort((a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf());
    return { published, upcoming };
  }, [events]);

  const newsStats = useMemo(() => {
    const published = news.filter((n) => n.isPublished).length;
    const latest = [...news]
      .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf());
    return { published, latest };
  }, [news]);

  const quickActions = [
    {
      label: 'Create event',
      icon: <EventAvailableOutlinedIcon />,
      onClick: () => navigate(RouteNameEnum.event.create),
      color: BBA_PRIMARY,
    },
    {
      label: 'Create news',
      icon: <NewspaperOutlinedIcon />,
      onClick: () => navigate(RouteNameEnum.news.create),
      color: BBA_PRIMARY,
    },
    {
      label: 'Send notification',
      icon: <CampaignOutlinedIcon />,
      onClick: () => navigate(RouteNameEnum.notification.create),
      color: '#F59E0B',
    },
    {
      label: 'View customers',
      icon: <PersonAddAltOutlinedIcon />,
      onClick: () => navigate(RouteNameEnum.customer.index),
      color: '#10B981',
    },
    {
      label: 'Featured content',
      icon: <StarOutlineOutlinedIcon />,
      onClick: () => navigate(RouteNameEnum.featuredContent.index),
      color: '#8B5CF6',
    },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Text style={{ color: '#999' }}>
          {DateTimeUtils.dateFormatStartWithDay(new Date())}
        </Text>
        <Title level={2} style={{ marginTop: 4, marginBottom: 0, color: '#1a1a1a', fontWeight: 700 }}>
          {`${greetByHour()}, ${user?.displayName || 'Admin'}`}
          <span style={{ marginLeft: 8 }}>👋</span>
        </Title>
        <Text style={{ color: '#666', marginTop: 4, display: 'block' }}>
          {user?.email || ''}
        </Text>
      </div>

      {/* KPI Cards */}
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <StatCard
            icon={<GroupsOutlinedIcon />}
            label="Total customers"
            value={customerTotal.toLocaleString()}
            loading={loadingCustomers}
            accent="#10B981"
            to={RouteNameEnum.customer.index}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard
            icon={<EventAvailableOutlinedIcon />}
            label="Events"
            value={events.length}
            caption={`${eventStats.published} published · ${eventStats.upcoming.length} upcoming`}
            loading={loadingEvents}
            accent={BBA_PRIMARY}
            to={RouteNameEnum.event.index}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard
            icon={<NewspaperOutlinedIcon />}
            label="News articles"
            value={news.length}
            caption={`${newsStats.published} published`}
            loading={loadingNews}
            accent={BBA_PRIMARY}
            to={RouteNameEnum.news.index}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <StatCard
            icon={<NotificationsActiveOutlinedIcon />}
            label="Notifications sent"
            value={notificationTotal.toLocaleString()}
            loading={loadingNotifications}
            accent="#F59E0B"
            to={RouteNameEnum.notification.index}
          />
        </Col>
      </Row>

      {/* Upcoming Events + Latest News */}
      <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={14}>
          <Card
            style={{ borderRadius: 12, border: '1px solid #EEE', height: '100%' }}
            styles={{ body: { padding: 24 } }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
            }}
            >
              <Title level={5} style={{ margin: 0, fontWeight: 700 }}>Upcoming events</Title>
              <div
                onClick={() => navigate(RouteNameEnum.event.index)}
                style={{
                  display: 'flex', alignItems: 'center', color: BBA_PRIMARY, cursor: 'pointer', fontSize: 14,
                }}
              >
                View all
                <ArrowForwardIcon style={{ fontSize: 16, marginLeft: 4 }} />
              </div>
            </div>
            <Divider style={{ marginBottom: 8 }} />
            {loadingEvents ? (
              [1, 2, 3].map((k) => (
                <div key={k} style={{ display: 'flex', padding: '12px 0' }}>
                  <Skeleton.Image active style={{ width: 64, height: 64 }} />
                  <div style={{ marginLeft: 16, flex: 1 }}>
                    <Skeleton active title={false} paragraph={{ rows: 2, width: ['60%', '40%'] }} />
                  </div>
                </div>
              ))
            ) : eventStats.upcoming.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <Text style={{ color: '#999' }}>No upcoming events</Text>
              </div>
            ) : (
              eventStats.upcoming.slice(0, 5).map((e) => (
                <div
                  key={e.id}
                  onClick={() => navigate(PathBuilder.buildPath(RouteNameEnum.event.detail, { params: { id: e.id } }))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 8px',
                    cursor: 'pointer',
                    borderRadius: 8,
                    transition: 'background-color .15s ease',
                  }}
                  onMouseEnter={(ev) => { ev.currentTarget.style.backgroundColor = '#FAFAFA'; }}
                  onMouseLeave={(ev) => { ev.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {e.coverImageUrl ? (
                    <img
                      src={e.coverImageUrl}
                      alt=""
                      style={{
                        width: 64, height: 64, borderRadius: 8, objectFit: 'cover',
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 64, height: 64, borderRadius: 8, backgroundColor: '#F5F5F5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    >
                      <EventAvailableOutlinedIcon style={{ color: '#BBB' }} />
                    </div>
                  )}
                  <div style={{ marginLeft: 16, flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 14,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {e.titleML?.en || e.titleML?.th || '-'}
                    </div>
                    <div style={{
                      display: 'flex', alignItems: 'center', marginTop: 4, gap: 8,
                    }}
                    >
                      <Text style={{ color: '#666', fontSize: 12 }}>
                        {DateTimeUtils.dateFormatStartWithDay(e.startAt)}
                      </Text>
                      {e.category && (
                        <Tag
                          color={categoryColors[e.category] || 'default'}
                          style={{ fontSize: 11, lineHeight: '18px', marginInlineEnd: 0 }}
                        >
                          {e.category}
                        </Tag>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </Card>
        </Col>

        <Col xs={24} md={10}>
          <Card
            style={{ borderRadius: 12, border: '1px solid #EEE', height: '100%' }}
            styles={{ body: { padding: 24 } }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
            }}
            >
              <Title level={5} style={{ margin: 0, fontWeight: 700 }}>Latest news</Title>
              <div
                onClick={() => navigate(RouteNameEnum.news.index)}
                style={{
                  display: 'flex', alignItems: 'center', color: BBA_PRIMARY, cursor: 'pointer', fontSize: 14,
                }}
              >
                View all
                <ArrowForwardIcon style={{ fontSize: 16, marginLeft: 4 }} />
              </div>
            </div>
            <Divider style={{ marginBottom: 8 }} />
            {loadingNews ? (
              [1, 2, 3].map((k) => (
                <div key={k} style={{ padding: '12px 0' }}>
                  <Skeleton active title={false} paragraph={{ rows: 2, width: ['80%', '30%'] }} />
                </div>
              ))
            ) : newsStats.latest.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center' }}>
                <Text style={{ color: '#999' }}>No news yet</Text>
              </div>
            ) : (
              newsStats.latest.slice(0, 5).map((n) => (
                <div
                  key={n.id}
                  onClick={() => navigate(PathBuilder.buildPath(RouteNameEnum.news.detail, { params: { id: n.id } }))}
                  style={{
                    padding: '10px 8px',
                    cursor: 'pointer',
                    borderRadius: 8,
                    transition: 'background-color .15s ease',
                  }}
                  onMouseEnter={(ev) => { ev.currentTarget.style.backgroundColor = '#FAFAFA'; }}
                  onMouseLeave={(ev) => { ev.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {n.titleML?.en || n.titleML?.th || '-'}
                  </div>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginTop: 4,
                  }}
                  >
                    <Text style={{ color: '#999', fontSize: 12 }}>
                      {DateTimeUtils.dateFormatStartWithDay(n.createdAt)}
                    </Text>
                    <Tag
                      color={n.isPublished ? 'success' : 'default'}
                      style={{ fontSize: 10, lineHeight: '16px', marginInlineEnd: 0 }}
                    >
                      {n.isPublished ? 'Published' : 'Draft'}
                    </Tag>
                  </div>
                </div>
              ))
            )}
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card
        style={{ borderRadius: 12, border: '1px solid #EEE' }}
        styles={{ body: { padding: 24 } }}
      >
        <Title level={5} style={{ marginTop: 0, marginBottom: 16, fontWeight: 700 }}>
          Quick actions
        </Title>
        <div
          style={{
            display: 'grid',
            gap: 16,
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          }}
        >
          {quickActions.map((action) => (
            <div
              key={action.label}
              onClick={action.onClick}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                borderRadius: 8,
                border: '1px dashed #DDD',
                cursor: 'pointer',
                transition: 'all .15s ease',
              }}
              onMouseEnter={(ev) => {
                ev.currentTarget.style.borderColor = action.color;
                ev.currentTarget.style.backgroundColor = `${action.color}08`;
              }}
              onMouseLeave={(ev) => {
                ev.currentTarget.style.borderColor = '#DDD';
                ev.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${action.color}15`,
                  color: action.color,
                  marginBottom: 8,
                }}
              >
                {action.icon}
              </div>
              <Text style={{ fontWeight: 600, textAlign: 'center' }}>
                {action.label}
              </Text>
              <AddIcon style={{ fontSize: 14, color: '#BBB', marginTop: 4 }} />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default DashboardHome;
